class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.recentSearchesKey = 'scatta_recent_collection_searches';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onPredictiveClick.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  normalizeSmartQuery(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\b(pbiettivi|obbiettivi|obietivi|obbiettivo|obietivo)\b/g, ' obiettivi ')
      .replace(/\b(lente|lenti|lens|lenses)\b/g, ' obiettivo ')
      // Obiettivi: la ricerca deve funzionare anche se l'utente non scrive "mm" o "f/".
      // Esempio: "rf 50mm 1.8", "rf 50 1.8" e "rf 50mm f 1.8" diventano confrontabili.
      .replace(/(\d+)\s*mm\b/g, ' $1 ')
      .replace(/\bf\s*\/?\s*(\d)/g, ' $1')
      .replace(/(\d+)[\.,](\d+)/g, ' $1 $2 ')
      .replace(/\b(mm|millimetri|millimetro|f)\b/g, ' ')
      .replace(/\b(macchina fotografica|macchine fotografiche|fotocamera|fotocamere|camera|cameras|usato|usata|usati|usate|mirrorless|reflex|dslr|body|corpo|kit|solo|compatibile|compatibili|mount|attacco)\b/g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  getSearchVariants(searchTerm) {
    const original = String(searchTerm || '').trim();
    const smart = this.normalizeSmartQuery(original);
    const variants = [];

    if (original) variants.push(original);
    if (smart && smart !== original.toLowerCase()) variants.push(smart);

    const noBrandNoise = smart
      .replace(/\b(canon|nikon|sony|fujifilm|fuji|panasonic|leica|olympus|pentax|sigma|tamron)\b/g, (m) => m)
      .trim();
    if (noBrandNoise && !variants.includes(noBrandNoise)) variants.push(noBrandNoise);

    return [...new Set(variants)].slice(0, 3);
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.renderRecentSearches();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    if (!query.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }

    if (query.length) {
      this.saveRecentSearch(query);
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.renderRecentSearches();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.renderRecentSearches();
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onPredictiveClick(event) {
    const collectionLink = event.target.closest('[data-scatta-collection-result]');
    if (collectionLink) {
      const text = collectionLink.textContent.trim();
      if (text) this.saveRecentSearch(text);
      return;
    }

    const recentButton = event.target.closest('[data-scatta-recent-search]');
    if (recentButton) {
      const value = recentButton.getAttribute('data-scatta-recent-search') || '';
      this.input.value = value;
      this.searchTerm = '';
      this.onChange();
      this.input.focus();
      return;
    }

    const removeButton = event.target.closest('[data-scatta-remove-recent]');
    if (removeButton) {
      event.preventDefault();
      event.stopPropagation();
      this.removeRecentSearch(removeButton.getAttribute('data-scatta-remove-recent') || '');
      this.renderRecentSearches();
      return;
    }

    const clearButton = event.target.closest('[data-scatta-clear-recents]');
    if (clearButton) {
      event.preventDefault();
      this.clearRecentSearches();
      this.renderRecentSearches();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id || '');
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button.predictive-search__item, button[aria-selected="true"], button.predictive-search__item[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const variants = this.getSearchVariants(searchTerm);
    const queryKey = variants.join('__').replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.fetchSearchVariant(variants, 0, queryKey);
  }

  fetchSearchVariant(variants, index, queryKey) {
    const term = variants[index] || variants[0] || '';

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[type]=collection&resources[limit]=20&resources[options][fields]=title`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const section = parsed.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = section ? section.innerHTML : '';
        const hasCollections = !!parsed.querySelector('#predictive-search-results-collections-list .predictive-search__list-item');

        if (!hasCollections && index + 1 < variants.length) {
          this.fetchSearchVariant(variants, index + 1, queryKey);
          return;
        }

        // Prima proviamo la ricerca intelligente sulle collection reali del negozio.
        // Così query come "rf 50 1.8" trovano "Canon RF 50mm f/1.8 STM"
        // anche quando la predictive search nativa di Shopify restituisce risultati deboli.
        this.renderFuzzyCollectionResults(this.searchTerm || this.getQuery(), queryKey).then((found) => {
          if (found) return;
          this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          });
          this.renderSearchResults(resultsMarkup);
        });
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }



  async getAllCollectionsForFuzzySearch() {
    if (this._scattaCollectionsCache) return this._scattaCollectionsCache;

    const collections = [];
    for (let page = 1; page <= 5; page += 1) {
      try {
        const response = await fetch(`/collections.json?limit=250&page=${page}`, { credentials: 'same-origin' });
        if (!response.ok) break;
        const data = await response.json();
        const batch = Array.isArray(data.collections) ? data.collections : [];
        collections.push(...batch);
        if (batch.length < 250) break;
      } catch (error) {
        break;
      }
    }

    this._scattaCollectionsCache = collections;
    return collections;
  }

  levenshteinDistance(a, b) {
    a = String(a || '');
    b = String(b || '');
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;

    const matrix = Array.from({ length: b.length + 1 }, (_, i) => [i]);
    for (let j = 0; j <= a.length; j += 1) matrix[0][j] = j;

    for (let i = 1; i <= b.length; i += 1) {
      for (let j = 1; j <= a.length; j += 1) {
        matrix[i][j] = b.charAt(i - 1) === a.charAt(j - 1)
          ? matrix[i - 1][j - 1]
          : Math.min(matrix[i - 1][j - 1] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j] + 1);
      }
    }
    return matrix[b.length][a.length];
  }

  tokenLooksSimilar(queryToken, titleToken) {
    if (!queryToken || !titleToken) return false;
    if (titleToken.includes(queryToken) || queryToken.includes(titleToken)) return true;
    const maxLen = Math.max(queryToken.length, titleToken.length);
    if (maxLen <= 4) return this.levenshteinDistance(queryToken, titleToken) <= 1;
    if (maxLen <= 8) return this.levenshteinDistance(queryToken, titleToken) <= 2;
    return this.levenshteinDistance(queryToken, titleToken) <= 3;
  }

  scoreCollectionMatch(collection, searchTerm) {
    const query = this.normalizeSmartQuery(searchTerm);
    const title = this.normalizeSmartQuery(collection.title || '');
    if (!query || !title) return 0;

    if (title === query) return 1000;
    if (title.includes(query)) return 850;

    const queryTokens = query.split(' ').filter(Boolean);
    const titleTokens = title.split(' ').filter(Boolean);
    if (!queryTokens.length || !titleTokens.length) return 0;

    let score = 0;
    for (const qToken of queryTokens) {
      let best = 0;
      for (const tToken of titleTokens) {
        if (tToken === qToken) best = Math.max(best, 120);
        else if (tToken.includes(qToken) || qToken.includes(tToken)) best = Math.max(best, 90);
        else if (this.tokenLooksSimilar(qToken, tToken)) best = Math.max(best, 65);
      }
      if (!best) return 0;
      score += best;
    }

    if (titleTokens.length > queryTokens.length) score -= Math.min(40, (titleTokens.length - queryTokens.length) * 7);
    return score;
  }

  async renderFuzzyCollectionResults(searchTerm, queryKey) {
    const collections = await this.getAllCollectionsForFuzzySearch();
    const matches = collections
      .map((collection) => ({ collection, score: this.scoreCollectionMatch(collection, searchTerm) }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score || String(a.collection.title).localeCompare(String(b.collection.title)))
      .slice(0, 20)
      .map((item) => item.collection);

    if (!matches.length) return false;

    const items = matches.map((collection, index) => {
      const title = this.escapeHTML(collection.title || '');
      const handle = this.escapeHTML(collection.handle || '');
      return `
        <li id="predictive-search-option-fuzzy-collection-${index}" class="predictive-search__list-item" role="option" aria-selected="false">
          <a href="/collections/${handle}" class="predictive-search__item link link--text" tabindex="-1" data-scatta-collection-result>
            <div class="predictive-search__item-content predictive-search__item-content--centered">
              <p class="predictive-search__item-heading h5">${title}</p>
            </div>
          </a>
        </li>`;
    }).join('');

    const resultsMarkup = `
      <div id="predictive-search-results" role="listbox" data-scatta-search-term="${this.escapeHTML(searchTerm)}">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--collections-only">
          <div class="predictive-search__result-group predictive-search__result-group--collections-only">
            <h2 id="predictive-search-collections" class="predictive-search__heading text-body caption-with-letter-spacing">Ricerche recenti</h2>
            <ul id="predictive-search-results-collections-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-collections">
              ${items}
            </ul>
          </div>
        </div>
      </div>
      <span class="hidden" data-predictive-search-live-region-count-value>${matches.length} collezioni trovate</span>`;

    this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
      predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
    });
    this.renderSearchResults(resultsMarkup);
    return true;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.predictiveSearchResults.dataset.scattaSearchTerm = this.searchTerm || this.getQuery();
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const status = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (status) this.setLiveRegionText(status.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = Math.min(
      window.innerHeight - (document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0),
      Math.round(window.innerHeight * 0.72),
      620
    );
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }

  getRecentSearches() {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.recentSearchesKey) || '[]');
      return Array.isArray(parsed) ? parsed.filter(Boolean).slice(0, 8) : [];
    } catch (error) {
      return [];
    }
  }

  saveRecentSearch(term) {
    const cleanTerm = String(term || '').trim();
    if (!cleanTerm) return;

    const current = this.getRecentSearches().filter((item) => item.toLowerCase() !== cleanTerm.toLowerCase());
    current.unshift(cleanTerm);
    localStorage.setItem(this.recentSearchesKey, JSON.stringify(current.slice(0, 8)));
  }

  removeRecentSearch(term) {
    const cleanTerm = String(term || '').trim().toLowerCase();
    const current = this.getRecentSearches().filter((item) => item.toLowerCase() !== cleanTerm);
    localStorage.setItem(this.recentSearchesKey, JSON.stringify(current));
  }

  clearRecentSearches() {
    localStorage.removeItem(this.recentSearchesKey);
  }

  escapeHTML(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  renderRecentSearches() {
    const recents = this.getRecentSearches();

    if (!recents.length) {
      this.closeResults(false);
      return;
    }

    const items = recents
      .map((term, index) => {
        const safeTerm = this.escapeHTML(term);
        return `
          <li id="predictive-search-recent-${index}" class="predictive-search__list-item predictive-search__recent-item" role="option" aria-selected="false">
            <button type="button" class="predictive-search__item predictive-search__recent-button" data-scatta-recent-search="${safeTerm}" tabindex="-1">
              <span class="predictive-search__item-heading h5">${safeTerm}</span>
            </button>
            <button type="button" class="predictive-search__recent-remove" data-scatta-remove-recent="${safeTerm}" aria-label="Elimina ricerca ${safeTerm}">×</button>
          </li>`;
      })
      .join('');

    this.predictiveSearchResults.innerHTML = `
      <div id="predictive-search-results" role="listbox" data-scatta-recent-results="true">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--collections-only">
          <div class="predictive-search__result-group predictive-search__result-group--collections-only">
            <h2 class="predictive-search__heading text-body caption-with-letter-spacing">Ricerche recenti</h2>
            <ul id="predictive-search-results-recents-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-recents">
              ${items}
            </ul>
            <button type="button" class="predictive-search__clear-recents" data-scatta-clear-recents>Cancella ricerche recenti</button>
          </div>
        </div>
      </div>
      <span class="hidden" data-predictive-search-live-region-count-value>${recents.length} ricerche recenti</span>`;

    this.setAttribute('results', true);
    this.removeAttribute('loading');
    this.open();
  }
}

customElements.define('predictive-search', PredictiveSearch);
